import { QueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { AssociationType } from "@/shared/domain/association";
import { FileRecord, IngestionStatus } from "@/shared/domain/files";
import { SessionRecord } from "@/shared/domain/sessions";
import { TopicRecord, MessagePathRecord } from "@/shared/domain/topics";
import * as UseFileTopics from "@/shared/domain/topics/hooks/useFileTopics";
import { isAbortError } from "@/shared/errors";
import {
  RobotoNotFoundException,
  RobotoUnauthorizedException,
} from "@/shared/http";
import { ErrorMonitoringService } from "@/shared/services";
import { Datasource } from "@/shared/state/visualization";
import {
  DatasetSource,
  FileSource,
  SessionSource,
} from "@/shared/state/visualization/schema/v1";

import {
  EphemeralWorkspaceState,
  Session,
  SessionClipRange,
  SessionLoadFailure,
} from "./EphemeralWorkspaceState";

const SESSION_FILE_PAGE_SIZE = 1000;

export type WorkspaceData = Pick<
  EphemeralWorkspaceState,
  "files" | "sessions" | "sessionLoadFailures" | "topics" | "messagePaths"
>;

/**
 * A workspace holding nothing: the value every collection starts from. Used
 * before any datasource has resolved, for a datasource kind the workspace has
 * none of, and as the base a failed session read builds its result on, so a
 * caller always has all five collections rather than some of them.
 */
export function emptyWorkspaceData(): WorkspaceData {
  return {
    files: [],
    sessions: [],
    sessionLoadFailures: [],
    topics: [],
    messagePaths: [],
  };
}

/**
 * Order two sessions by start time (`min_timestamp_ns`), earliest first, with
 * sessions that have no bound last. It is a `bigint`, so compare with relational
 * operators rather than numeric subtraction.
 */
function compareSessionStart(a: SessionRecord, b: SessionRecord): number {
  if (a.min_timestamp_ns === null && b.min_timestamp_ns === null) {
    return 0;
  }
  if (a.min_timestamp_ns === null) {
    return 1;
  }
  if (b.min_timestamp_ns === null) {
    return -1;
  }
  if (a.min_timestamp_ns < b.min_timestamp_ns) {
    return -1;
  }
  if (a.min_timestamp_ns > b.min_timestamp_ns) {
    return 1;
  }
  return 0;
}

/**
 * Everything the HTTP layer does not raise as a missing or unauthorized
 * session, from a network drop to a response the client could not parse, lands
 * in `unreadable`.
 */
function failureReason(error: unknown): SessionLoadFailure["reason"] {
  if (error instanceof RobotoNotFoundException) {
    return "not-found";
  }
  if (error instanceof RobotoUnauthorizedException) {
    return "unauthorized";
  }
  return "unreadable";
}

/** First occurrence of each item, keyed by `keyOf`. */
function dedupeBy<T>(items: T[], keyOf: (item: T) => string): T[] {
  const seen = new Set<string>();
  const result: T[] = [];
  for (const item of items) {
    const key = keyOf(item);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(item);
  }
  return result;
}

export class WorkspaceDataService {
  private domainServices: ReturnType<typeof useDomainServices>;
  private queryClient: QueryClient;
  private resourceOwnerId: string | undefined;

  constructor(
    domainServices: ReturnType<typeof useDomainServices>,
    queryClient: QueryClient,
    resourceOwnerId?: string,
  ) {
    this.domainServices = domainServices;
    this.queryClient = queryClient;
    this.resourceOwnerId = resourceOwnerId;
  }

  async fetchWorkspaceData(
    datasources: Datasource[],
    abortController: AbortController,
  ): Promise<WorkspaceData> {
    const { files, datasets, sessions } = this.partitionDatasource(datasources);

    if (datasets.length > 1) {
      throw new Error(
        "Unable to visualize multiple datasets at once. Please select files from a single dataset or visualize each dataset separately.",
      );
    }

    const datasetData =
      datasets.length === 1
        ? await this.fetchDatasetWorkspaceData(
            datasets[0].value.datasetId,
            abortController,
          )
        : emptyWorkspaceData();

    const datasetFileIds = new Set(datasetData.files.map((f) => f.file_id));
    const additionalFileIds = files
      .map((f) => f.value.fileId)
      .filter((id) => !datasetFileIds.has(id));

    const fileData =
      additionalFileIds.length > 0
        ? await this.fetchFileWorkspaceData(additionalFileIds, abortController)
        : emptyWorkspaceData();

    // Only an abort rejects; every other failure comes back as a value.
    const sessionData = await Promise.all(
      sessions.map((session) =>
        this.fetchSessionWorkspaceData(
          session.value.sessionId,
          abortController,
        ),
      ),
    );

    return this.mergeWorkspaceData(datasetData, fileData, ...sessionData);
  }

  /**
   * Merge parts into one workspace. The flat `files`, `topics`, and
   * `messagePaths` are deduped by id (a file shared by two sessions is fetched
   * per session), but each `Session.files` is left intact so a shared file still
   * appears under every session. Sessions are sorted once, by start time.
   */
  private mergeWorkspaceData(...parts: WorkspaceData[]): WorkspaceData {
    return {
      files: dedupeBy(
        parts.flatMap((part) => part.files),
        (file) => file.file_id,
      ).sort((x, y) =>
        x.relative_path.localeCompare(y.relative_path, undefined, {
          sensitivity: "base",
        }),
      ),
      sessions: parts
        .flatMap((part) => part.sessions)
        .sort((a, b) => compareSessionStart(a.record, b.record)),
      sessionLoadFailures: parts.flatMap((part) => part.sessionLoadFailures),
      topics: dedupeBy(
        parts.flatMap((part) => part.topics),
        (topic) => topic.topic_id,
      ),
      messagePaths: dedupeBy(
        parts.flatMap((part) => part.messagePaths),
        (messagePath) => messagePath.message_path_id,
      ),
    };
  }

  private extractMessagePaths(topics: TopicRecord[]): MessagePathRecord[] {
    return topics.flatMap((topic) => topic.message_paths);
  }

  private async fetchDatasetWorkspaceData(
    datasetId: string,
    abortController: AbortController,
  ): Promise<WorkspaceData> {
    const fileRecords = await this.domainServices.datasetService.listAllFiles(
      datasetId,
      {
        abortSignal: abortController.signal,
      },
    );

    const ingestedFileRecords = this.getIngestedFiles(fileRecords);

    const topicRecords = await this.fetchTopicsForFiles(
      ingestedFileRecords.map((fileRecord) => fileRecord.file_id),
    );

    return {
      files: ingestedFileRecords,
      sessions: [],
      sessionLoadFailures: [],
      topics: topicRecords,
      messagePaths: this.extractMessagePaths(topicRecords),
    };
  }

  private async fetchFileWorkspaceData(
    fileIds: string[],
    abortController: AbortController,
  ): Promise<WorkspaceData> {
    const [fileRecords, topicRecords] = await Promise.all([
      Promise.all(
        fileIds.map((fileId) =>
          this.domainServices.files.getFileRecord(fileId, {
            abortSignal: abortController.signal,
          }),
        ),
      ),
      this.fetchTopicsForFiles(fileIds),
    ]);

    return {
      files: fileRecords,
      sessions: [],
      sessionLoadFailures: [],
      topics: topicRecords,
      messagePaths: this.extractMessagePaths(topicRecords),
    };
  }

  /**
   * Expand one session datasource into its record and files. Reads the record
   * (name + `min_timestamp_ns`) in parallel with the session's file listing,
   * loads those files and their topics through `fetchFileWorkspaceData`, and
   * packages the result as a single {@link Session}. The listing also reports
   * each membership's clip range, kept as `clipRangeByFileId` so deriving
   * alignment sources can narrow a clipped file's span.
   *
   * A failed read returns a {@link SessionLoadFailure} rather than throwing, so the
   * session contributes nothing but the rest of the workspace still opens; an
   * abort propagates.
   */
  private async fetchSessionWorkspaceData(
    sessionId: string,
    abortController: AbortController,
  ): Promise<WorkspaceData> {
    try {
      const [record, sessionFiles] = await Promise.all([
        this.domainServices.sessionService.getSessionById(sessionId, {
          abortSignal: abortController.signal,
          resourceOwnerId: this.resourceOwnerId,
        }),
        this.domainServices.sessionService.listAllSessionFiles(sessionId, {
          limit: SESSION_FILE_PAGE_SIZE,
          resourceOwnerId: this.resourceOwnerId,
          abortSignal: abortController.signal,
        }),
      ]);

      const fileData = await this.fetchFileWorkspaceData(
        sessionFiles.map((file) => file.file_id),
        abortController,
      );

      const clipRangeByFileId = new Map<string, SessionClipRange>();
      for (const file of sessionFiles) {
        const { range_min_timestamp_ns, range_max_timestamp_ns } = file;
        if (
          range_min_timestamp_ns === null ||
          range_max_timestamp_ns === null
        ) {
          continue;
        }
        clipRangeByFileId.set(file.file_id, {
          min: range_min_timestamp_ns,
          max: range_max_timestamp_ns,
        });
      }

      const session: Session = {
        id: sessionId,
        record,
        files: fileData.files,
        clipRangeByFileId,
      };

      return { ...fileData, sessions: [session] };
    } catch (error) {
      if (abortController.signal.aborted || isAbortError(error)) {
        throw error;
      }
      ErrorMonitoringService.captureError(error);
      return {
        ...emptyWorkspaceData(),
        sessionLoadFailures: [{ sessionId, reason: failureReason(error) }],
      };
    }
  }

  private async fetchTopicsForFiles(fileIds: string[]): Promise<TopicRecord[]> {
    const topicRecordsPerFile = await Promise.all(
      fileIds.map((fileId) =>
        this.queryClient.ensureQueryData({
          queryKey: UseFileTopics.getQueryKey(fileId),
          queryFn: ({ signal }) =>
            this.domainServices.topics.getTopicsByAssociation(
              {
                association_id: fileId,
                association_type: AssociationType.File,
              },
              {
                abortSignal: signal,
              },
            ),
        }),
      ),
    );
    return topicRecordsPerFile.flat();
  }
  private getIngestedFiles(fileRecords: FileRecord[]): FileRecord[] {
    return fileRecords.filter(
      (fileRecord) =>
        fileRecord.ingestion_status !== IngestionStatus.NotIngested,
    );
  }

  private partitionDatasource(datasources: Datasource[]): {
    files: FileSource[];
    datasets: DatasetSource[];
    sessions: SessionSource[];
  } {
    return datasources.reduce<{
      files: FileSource[];
      datasets: DatasetSource[];
      sessions: SessionSource[];
    }>(
      (data, datasource) => {
        if (datasource.type === "file") {
          data.files.push(datasource);
        } else if (datasource.type === "session") {
          data.sessions.push(datasource);
        } else {
          data.datasets.push(datasource);
        }
        return data;
      },
      {
        files: [],
        datasets: [],
        sessions: [],
      },
    );
  }
}
